import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Job, type JobMatches } from "../lib/api";
import { displaySkill } from "../lib/format";
import { googleUrl, WEB_SITES, webQuery, type WebSite } from "../lib/websearch";
import { candidateLine } from "./Candidates";
import { CandidateForm } from "./CandidateForm";

const pad = (n: number) => String(n).padStart(2, "0");
const SHOWN = 8;

/**
 * Finding people for a job: first who in the database fits, then a web search for who isn't in
 * it yet. `changed` is bumped by the page whenever the pipeline changes, so someone just added
 * drops out of the suggestions; `onAdded` tells the page to refresh the pipeline.
 */
export function JobSourcing({ job, changed, onAdded }: { job: Job; changed: number; onAdded: () => void }) {
  const [data, setData] = useState<JobMatches | null>(null);
  const load = useAction();
  const reload = useCallback(
    () =>
      load.run(async () => {
        setData(await api.jobMatches(job.id));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [job.id, job.title, job.location, job.skills.join(","), changed],
  );
  useEffect(() => void reload(), [reload]);

  const open = job.status === "open" || job.status === "on_hold";
  if (!open) return null;

  return (
    <>
      <Matches job={job} data={data} error={load.error} onAdded={onAdded} />
      {data && <WebSearchPanel job={job} role={data.role} place={data.place} onAdded={onAdded} />}
    </>
  );
}

function Matches({ job, data, error, onAdded }: { job: Job; data: JobMatches | null; error: string | null; onAdded: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const add = useAction();
  const list = data?.candidates ?? [];
  const shown = showAll ? list : list.slice(0, SHOWN);

  return (
    <section className="section" aria-labelledby="matches-h">
      <div className="section-head">
        <h2 id="matches-h" className="label">
          Suggested from your candidates
        </h2>
        {data && <span className="label figures">{pad(list.length)} found</span>}
      </div>
      <ErrorNotice error={error ?? add.error} />
      {job.skills.length === 0 && (
        <p className="hint">Matching on the job title only. Add the skills this job needs (Edit, under Details) to match on skills and CVs too.</p>
      )}
      {data && list.length === 0 && (
        <p className="empty-line">No one in your database fits yet. Try a web search below.</p>
      )}
      {list.length > 0 && (
        <ol className="index matches">
          {shown.map((c, i) => (
            <li key={c.id}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <Link className="primary" to={`/candidates/${c.id}`}>
                  {c.fullName}
                </Link>
                <span className="secondary">{candidateLine(c)}</span>
                <span className="chips small" aria-label="Why they match">
                  {c.match.title && <span className="chip hit">Title fits</span>}
                  {c.match.skills.map((s) => (
                    <span key={s} className="chip hit">
                      {displaySkill(s)}
                    </span>
                  ))}
                  {c.match.skillsInText.map((s) => (
                    <span key={s} className="chip hit" title="Mentioned in their CV or notes">
                      {displaySkill(s)} <span className="match-where">in CV</span>
                    </span>
                  ))}
                  {c.match.nearby && <span className="chip">Nearby</span>}
                  {c.match.missing.length > 0 && (
                    <span className="match-missing">no {c.match.missing.map(displaySkill).join(", ")}</span>
                  )}
                </span>
              </span>
              <span className="actions">
                <button
                  type="button"
                  className="link-btn"
                  disabled={add.pending}
                  onClick={() =>
                    void add.run(async () => {
                      await api.addToJob(job.id, c.id);
                      onAdded();
                    })
                  }
                >
                  Add to shortlist
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {list.length > SHOWN && (
        <button type="button" className="link-btn spaced-top" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${list.length}`}
        </button>
      )}
    </section>
  );
}

function WebSearchPanel({ job, role: startRole, place: startPlace, onAdded }: { job: Job; role: string; place: string | null; onAdded: () => void }) {
  const [site, setSite] = useState<WebSite>("linkedin");
  const [role, setRole] = useState(startRole);
  const [place, setPlace] = useState((startPlace ?? "").replace(/\b\p{L}/gu, (ch) => ch.toUpperCase()));
  // Two or three skills keep a search broad enough to return people; the recruiter adds more.
  const [skills, setSkills] = useState<string[]>(job.skills.slice(0, 2));
  const [anySkill, setAnySkill] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const add = useAction();

  useEffect(() => setQuery(webQuery({ site, role, place, skills, anySkill })), [site, role, place, skills, anySkill]);

  const toggle = (s: string) => setSkills((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <section className="section" aria-labelledby="web-h">
      <div className="section-head">
        <h2 id="web-h" className="label">
          Search the web for new people
        </h2>
      </div>
      <p className="hint">
        Builds a Google search for public profiles. It opens in a new tab; nothing is saved here until you add someone.
      </p>

      <div className="web-search">
        <fieldset className="segmented">
          <legend className="sr-only">Where to look</legend>
          {(Object.keys(WEB_SITES) as WebSite[]).map((s) => (
            <label key={s}>
              <input type="radio" name="web-site" checked={site === s} onChange={() => setSite(s)} />
              <span>{WEB_SITES[s].label}</span>
            </label>
          ))}
        </fieldset>
        <div className="grid-2">
          <label className="field">
            <span className="label">Role</span>
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. platform engineer" />
          </label>
          <label className="field">
            <span className="label">Location</span>
            <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Anywhere" />
          </label>
        </div>
        {job.skills.length > 0 && (
          <div className="skill-filter" role="group" aria-label="Skills to search for">
            <span className="label">Skills</span>
            <div className="chips">
              {job.skills.map((s) => (
                <button key={s} type="button" className="chip toggle" aria-pressed={skills.includes(s)} onClick={() => toggle(s)}>
                  {displaySkill(s)}
                </button>
              ))}
              {skills.length > 1 && (
                <label className="check inline">
                  <input type="checkbox" checked={anySkill} onChange={(e) => setAnySkill(e.target.checked)} />
                  <span>Any one of them</span>
                </label>
              )}
            </div>
          </div>
        )}
        <label className="field">
          <span className="label">Google search (edit it if you like)</span>
          <textarea className="query" rows={2} spellCheck={false} value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <div className="button-row">
          <a className="btn" href={googleUrl(query)} target="_blank" rel="noreferrer noopener">
            Search on Google
          </a>
          <button type="button" className="link-btn" onClick={() => setAdding((v) => !v)}>
            {adding ? "Cancel" : "+ Add someone you found"}
          </button>
        </div>
      </div>

      {adding && (
        <div className="spaced-top">
          <p className="notice">
            They didn't give you their details, so UK GDPR says you must send them a privacy notice within a month of adding them
            (sooner if you contact them first). Record the date below when you do.
          </p>
          <ErrorNotice error={add.error} />
          <CandidateForm
            initial={{ source: WEB_SITES[site].source }}
            submitLabel="Add and shortlist"
            onCancel={() => setAdding(false)}
            onSaved={(c) =>
              void add.run(async () => {
                await api.addToJob(job.id, c.id);
                setAdding(false);
                onAdded();
              })
            }
          />
        </div>
      )}
    </section>
  );
}
